import React from 'react';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Box, Typography, Divider } from '@mui/material';
import { motion } from 'framer-motion';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LogoutIcon from '@mui/icons-material/Logout';
import CottageIcon from '@mui/icons-material/Cottage';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import '../App.css';

const MotionListItem = motion(ListItem);

const ACCENT = '#FFFFFF';

const menuItems = [
    { text: 'Home', icon: <HomeIcon />, path: '/' },
    { text: 'Residents', icon: <PeopleIcon />, path: '/residents' },
    { text: 'HOA Invoices', icon: <ReceiptIcon />, path: '/invoices' },
    { text: 'HOA Dues', icon: <PaymentIcon />, path: '/payments' },
    { text: 'Bills Payable', icon: <ReceiptLongIcon />, path: '/bills-payable' },
    { text: 'Ledger', icon: <AccountBalanceIcon />, path: '/ledger' },
];

const listVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            duration: 0.2,
            when: "beforeChildren",
            staggerChildren: 0.05
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { duration: 0.1 }
    }
};

const Sidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout } = useAuth();

    const handleNavigation = (path) => {
        navigate(path);
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    return (
        <Drawer
            variant="permanent"
            sx={{
                width: 280,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: 280,
                    boxSizing: 'border-box',
                    backgroundColor: 'background.default',
                    borderRight: '1px solid',
                    borderRightColor: 'grey.800',
                }
            }}
        >
            <Box
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '24px 20px',
                        borderBottom: '1px solid',
                        borderBottomColor: 'grey.800',
                        gap: 1.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            bgcolor: 'grey.700',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CottageIcon sx={{ fontSize: 18, color: 'grey.300' }} />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'text.primary',
                            fontWeight: 600,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        myHOA Portal
                    </Typography>
                </Box>

                <List
                    component={motion.div}
                    variants={listVariants}
                    initial="hidden"
                    animate="visible"
                    sx={{
                        padding: '16px',
                        flex: 1,
                    }}
                >
                    {menuItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <MotionListItem
                                key={item.text}
                                variants={itemVariants}
                                onClick={() => handleNavigation(item.path)}
                                sx={{
                                    borderRadius: '12px',
                                    mb: 0.5,
                                    backgroundColor: isActive ? 'rgba(255, 255, 255, 0.06)' : 'transparent',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    pl: isActive ? 2.5 : 2,
                                    '&:hover': {
                                        backgroundColor: isActive ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.04)',
                                    },
                                    '&::before': isActive ? {
                                        content: '""',
                                        position: 'absolute',
                                        left: 0,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        width: 3,
                                        height: '60%',
                                        borderRadius: '0 3px 3px 0',
                                        backgroundColor: ACCENT,
                                    } : {},
                                }}
                            >
                                <ListItemIcon sx={{
                                    color: isActive ? 'text.primary' : 'grey.500',
                                    minWidth: 40,
                                }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.text}
                                    sx={{
                                        '& .MuiListItemText-primary': {
                                            color: isActive ? 'text.primary' : 'text.secondary',
                                            fontWeight: isActive ? 600 : 400,
                                        }
                                    }}
                                />
                            </MotionListItem>
                        );
                    })}

                    <Divider sx={{ my: 1.5, borderColor: 'grey.800' }} />

                    <MotionListItem
                        variants={itemVariants}
                        onClick={handleLogout}
                        sx={{
                            borderRadius: '12px',
                            cursor: 'pointer',
                            backgroundColor: 'transparent',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.04)',
                            },
                        }}
                    >
                        <ListItemIcon sx={{
                            color: 'grey.500',
                            minWidth: 40,
                        }}>
                            <LogoutIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary="Logout"
                            sx={{
                                '& .MuiListItemText-primary': {
                                    color: 'text.secondary',
                                }
                            }}
                        />
                    </MotionListItem>
                </List>

                <Box
                    sx={{
                        padding: '16px',
                        borderTop: '1px solid',
                        borderTopColor: 'grey.800',
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'grey.500',
                            fontSize: '0.75rem',
                        }}
                    >
                        Version 1.0P
                    </Typography>
                </Box>
            </Box>
        </Drawer>
    );
};

export default Sidebar;
